import { ChatCompletionRequestMessage } from "openai";
import { db, Settings } from "../db";
import { config } from "./config";

// Define the provider interface
export interface Provider {
  id: string;
  name: string;
  description: string;
  authType: "none" | "bearer-token" | "api-key";
  apiKeyUrl?: string;
  defaultModel: string;
  models: ProviderModel[];
  validateCredentials: (settings: Settings) => Promise<boolean>;
  createChatCompletion: (
    settings: Settings,
    messages: ChatCompletionRequestMessage[]
  ) => Promise<any>;
  createStreamChatCompletion: (
    settings: Settings,
    messages: ChatCompletionRequestMessage[],
    chatId: string,
    messageId: string
  ) => Promise<any>;
  // New method to fetch model list from the API
  fetchModels: (settings: Settings) => Promise<ProviderModel[]>;
}

// Define the provider model interface
export interface ProviderModel {
  id: string;
  name: string;
  description?: string;
  contextWindow?: number;
  maxTokens?: number;
}

// Provider registry to store all available providers
class ProviderRegistry {
  private providers: Map<string, Provider> = new Map();

  // Register a provider
  register(provider: Provider): void {
    this.providers.set(provider.id, provider);
  }

  // Get a provider by ID
  get(id: string): Provider | undefined {
    return this.providers.get(id);
  }

  // Get all registered providers
  getAll(): Provider[] {
    return Array.from(this.providers.values());
  }

  // Get provider models as options for UI select components
  getProviderModelsAsOptions(providerId: string): { value: string; label: string }[] {
    const provider = this.get(providerId);
    if (!provider) return [];
    
    return provider.models.map((model) => ({
      value: model.id,
      label: model.name,
    }));
  }

  // Get providers as options for UI select components
  getProvidersAsOptions(): { value: string; label: string }[] {
    return this.getAll().map((provider) => ({
      value: provider.id,
      label: provider.name,
    }));
  }
}

// Create and export the provider registry
export const providerRegistry = new ProviderRegistry();

// Helper function to get the current provider based on settings
export async function getCurrentProvider(): Promise<Provider | undefined> {
  const settings = await db.settings.get("general");
  if (!settings || !settings.provider) return undefined;
  
  return providerRegistry.get(settings.provider);
}

// Helper function to fetch models for the current provider and update the registry
export async function fetchModelsAndUpdateRegistry(providerId?: string): Promise<ProviderModel[]> {
  const settings = await db.settings.get("general");
  if (!settings) {
    throw new Error("Settings not found");
  }
  
  // Use specified provider ID or the current one from settings
  const providerToUse = providerId || settings.provider;
  
  if (!providerToUse) {
    throw new Error("No provider specified or configured");
  }

  const provider = providerRegistry.get(providerToUse);
  if (!provider) {
    throw new Error(`Provider ${providerToUse} not found`);
  }

  try {
    // Fetch models from the provider's API
    const fetchedModels = await provider.fetchModels(settings);
    
    // Update the provider's models array with the fetched models
    provider.models = fetchedModels;
    
    console.log(`Updated ${provider.name} models in registry:`, fetchedModels.length);
    
    // Check if the currently selected model exists in the new model list
    if (settings.provider === providerToUse && settings.model) {
      const modelExists = fetchedModels.some(m => m.id === settings.model);
      
      if (!modelExists && fetchedModels.length > 0) {
        // If the model doesn't exist but we have models, update to the first available model
        const newModel = fetchedModels[0].id;
        
        console.log(`Selected model ${settings.model} not found in provider's new models. Updating to ${newModel}`);
        
        // Update model in database
        await db.settings.update("general", { model: newModel });
      }
    }
    
    // Dispatch a custom event to notify components that models have been updated
    // Using setTimeout to ensure this happens after the current execution context
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('models-updated', {
        detail: {
          providerId: providerToUse,
          modelsCount: fetchedModels.length,
          modelChanged: !fetchedModels.some(m => m.id === settings.model) && fetchedModels.length > 0
        }
      }));
      
      // Set the fetchingModels state to false in any components that are listening
      window.dispatchEvent(new CustomEvent('models-fetch-complete'));
      
      console.log('Dispatched models-updated event');
    }, 0);
    
    return fetchedModels;
  } catch (error: any) {
    console.error(`Error fetching models for ${provider.name}:`, error);
    // Dispatch an event to notify components that fetching failed
    window.dispatchEvent(new CustomEvent('models-fetch-error', {
      detail: { error: error.message || "Failed to fetch models" }
    }));
    throw error;
  }
}

// Helper function to create a chat completion with the current provider
export async function createChatCompletion(
  messages: ChatCompletionRequestMessage[]
) {
  const settings = await db.settings.get("general");
  if (!settings || !settings.provider) {
    throw new Error("Provider not configured");
  }

  const provider = providerRegistry.get(settings.provider);
  if (!provider) {
    throw new Error(`Provider ${settings.provider} not found`);
  }

  return provider.createChatCompletion(settings, messages);
}

// Helper function to create a streaming chat completion with the current provider
export async function createStreamChatCompletion(
  messages: ChatCompletionRequestMessage[],
  chatId: string,
  messageId: string
) {
  const settings = await db.settings.get("general");
  if (!settings || !settings.provider) {
    throw new Error("Provider not configured");
  }

  const provider = providerRegistry.get(settings.provider);
  if (!provider) {
    throw new Error(`Provider ${settings.provider} not found`);
  }

  return provider.createStreamChatCompletion(settings, messages, chatId, messageId);
}

// Helper function to validate credentials with the current provider
export async function validateCredentials(settings: Settings): Promise<boolean> {
  if (!settings.provider) {
    throw new Error("Provider not specified");
  }

  const provider = providerRegistry.get(settings.provider);
  if (!provider) {
    throw new Error(`Provider ${settings.provider} not found`);
  }

  return provider.validateCredentials(settings);
}
